import React from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { GlassCard } from './GlassCard';

const Hero = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';

  const content = {
    badge: isAr
      ? 'رعاية منزلية معتمدة في الكويت'
      : 'Certified Home Care in Kuwait',
    titleMain: isAr ? 'رعاية صحية متميزة' : 'Premium Home',
    titleSub: isAr ? 'في راحة منزلك' : 'Healthcare Services',
    description: isAr
      ? 'نقدم أفضل خدمات الرعاية المنزلية، رعاية كبار السن، وتمريض حديثي الولادة في الكويت بأسعار تنافسية وفرق طبية متخصصة.'
      : 'Professional elderly care, newborn support, and home nursing delivered with compassion and expertise to your doorstep in Kuwait.',
    ctaCall: isAr ? 'اتصل الآن' : 'Call Now',
    ctaWA: 'WhatsApp',
    floating: {
      trust: isAr ? 'موثوق ومعتمد' : 'Trusted & Certified',
      support: isAr ? 'دعم ٢٤/٧' : '24/7 Support',
    },
  };

  return (
    <section
      className='relative min-h-screen flex items-center bg-[#050505] overflow-hidden' // Core Black Background
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Background Glows - Critical for "Depth" on Black */}
      <div className='absolute inset-0 pointer-events-none'>
        <div className='absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-medical-blue-600/10 rounded-full blur-[140px]' />
        <div className='absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-trust-teal/5 rounded-full blur-[120px]' />
      </div>

      <div className='relative section-padding py-24 w-full'>
        <div className='max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center'>
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: isAr ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge: Subtle Border & Transparent Fill */}
            <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-medical-blue-400 text-xs font-black uppercase tracking-widest mb-6'>
              <ShieldCheck size={16} />
              {content.badge}
            </div>

            <h1 className='text-5xl md:text-7xl font-black text-white leading-[1.1] mb-6'>
              {content.titleMain} <br />
              <span className='text-medical-blue-500'>{content.titleSub}</span>
            </h1>

            <p className='text-lg md:text-xl text-gray-400 mb-10 leading-relaxed max-w-xl'>
              {content.description}
            </p>

            <div className='flex flex-col sm:flex-row gap-4'>
              <a
                href='tel:+96550097296'
                className='flex items-center justify-center gap-3 px-10 py-5 bg-medical-blue-600 text-white rounded-2xl font-black text-lg shadow-xl shadow-medical-blue-600/20 hover:bg-medical-blue-500 hover:scale-105 active:scale-95 transition-all'
              >
                <Phone size={22} />
                {content.ctaCall}
              </a>
              <a
                href='https://wa.me/96550097296'
                className='flex items-center justify-center gap-3 px-10 py-5 bg-white/5 text-white border border-white/10 rounded-2xl font-black text-lg shadow-lg hover:bg-white/10 hover:scale-105 active:scale-95 transition-all backdrop-blur-md'
              >
                <MessageCircle size={22} className='text-green-500' />
                {content.ctaWA}
              </a>
            </div>

            {/* Quick Benefits: Dimmed text to keep focus on Headlines */}
            <div className='mt-12 flex items-center gap-6 text-gray-500 font-bold text-sm'>
              <div className='flex items-center gap-2'>
                <CheckCircle2 size={18} className='text-trust-teal/80' />
                {isAr ? 'ممرضات مرخصات' : 'Licensed Nurses'}
              </div>
              <div className='flex items-center gap-2'>
                <CheckCircle2 size={18} className='text-trust-teal/80' />
                {isAr ? 'رعاية مرنة' : 'Flexible Care'}
              </div>
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className='relative'
          >
            {/* Ambient Shadow/Glow behind the image to lift it off the black bg */}
            <div className='absolute -inset-4 bg-medical-blue-500/20 blur-3xl rounded-full opacity-50' />

            <div className='relative z-10 rounded-[3rem] overflow-hidden border-8 border-white/5 shadow-2xl'>
              <img
                src='/images/hero.jpg'
                alt='Healthcare Professional'
                className='w-full h-[500px] md:h-[600px] object-cover grayscale-[10%] contrast-[1.1]'
                loading='eager'
              />
              {/* Vignette to blend image edges into pure black */}
              <div className='absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/20' />
            </div>

            {/* Floating Status Card 1 */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className={`absolute -top-6 ${isAr ? '-left-6' : '-right-6'} z-20`}
            >
              <GlassCard className='px-6 py-4 flex items-center gap-4 border-white/10 bg-white/5'>
                <div className='p-3 bg-medical-blue-500 rounded-xl text-white'>
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <p className='text-[10px] font-bold text-medical-blue-400 uppercase'>
                    {isAr ? 'معتمد' : 'Verified'}
                  </p>
                  <p className='text-sm font-black text-white'>
                    {content.floating.trust}
                  </p>
                </div>
              </GlassCard>
            </motion.div>

            {/* Floating Status Card 2 */}
            <motion.div
              animate={{ y: [0, 15, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5,
              }}
              className={`absolute -bottom-6 ${isAr ? '-right-6' : '-left-6'} z-20`}
            >
              <GlassCard className='px-6 py-4 flex items-center gap-4 border-white/10 bg-white/5'>
                <div className='p-3 bg-trust-teal rounded-xl text-white'>
                  <Clock size={24} />
                </div>
                <div>
                  <p className='text-[10px] font-bold text-trust-teal uppercase'>
                    {isAr ? 'متاح' : 'Availability'}
                  </p>
                  <p className='text-sm font-black text-white'>
                    {content.floating.support}
                  </p>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
